import {all, call, put, select, takeLatest} from 'redux-saga/effects';
import * as ActionTypes from '../actions/types.action';
import ReviewsApi from '../api/reviews.api';
import {getBearerHeader} from '../helpers/api.helper';
import {GoogleAnalyticsHelper} from '../helpers/googleAnalytics.helper';
import {
  getPromptItems,
  getReviewError,
  getUnratedItemIds,
  isLockingReviewCode,
} from '../helpers/orderFeedback.helper';
import {buildReviewVariables} from './reviews.saga';
import {ANALYTICS_EVENTS, FEEDBACK_RESULT} from '../constants/constants';

const getAuth = state => state.auth;
const getOrderFeedbackState = state => state.orderFeedback;

// Names and images are a nicety: without them the popup still shows the
// server's names.
function* getProducts(itemIds, headers) {
  try {
    const result = yield call(
      ReviewsApi.getOrderFeedbackProducts,
      itemIds,
      headers
    );
    return (
      (result &&
        result.data &&
        result.data.searchProductsByItemIdIncludingDiscontinued &&
        result.data.searchProductsByItemIdIncludingDiscontinued.results) ||
      []
    );
  } catch (e) {
    return [];
  }
}

// The server picks the order and its products (invoiced 24 hours ago or more,
// not reviewed, not in a newer order) and keeps the snooze, so this only
// shows what it returns.
export function* getOrderFeedback(action) {
  try {
    const {accountId} = action;
    const auth = yield select(getAuth);
    const headers = getBearerHeader(null, auth);
    if (!accountId || !headers) {
      return;
    }
    const result = yield call(ReviewsApi.getReviewPrompt, headers);
    const prompt = result && result.data && result.data.reviewPrompt;
    const promptItems = (prompt && prompt.items) || [];
    if (!prompt || !prompt.orderId || !promptItems.length) {
      return;
    }
    const products = yield call(
      getProducts,
      promptItems.map(item => item.itemId),
      headers
    );
    const items = getPromptItems(promptItems, products);
    if (!items.length) {
      return;
    }
    yield put({
      type: ActionTypes.GET_ORDER_FEEDBACK_SUCCESS,
      accountId,
      orderId: prompt.orderId,
      items,
    });
  } catch (e) {
    // The popup is optional: a failure here just means it isn't shown.
    yield put({
      type: ActionTypes.GET_ORDER_FEEDBACK_FAILED,
      message: e.message,
    });
  }
}

/**
 * Tells the server the customer put the popup off: Maybe later, closing it,
 * or a submit leaving products unrated. Once per popup, because the server
 * stops asking after the order's second snooze; and never once nothing is
 * left to rate (all sent, or the rest locked by the service). A failure only
 * means the popup may come back sooner.
 */
export function* snoozeReviewPrompt() {
  const orderFeedback = yield select(getOrderFeedbackState);
  if (
    !orderFeedback ||
    !orderFeedback.orderId ||
    orderFeedback.snoozed ||
    orderFeedback.result === FEEDBACK_RESULT.SUCCESS ||
    !getUnratedItemIds(
      orderFeedback.items,
      [],
      orderFeedback.alreadyReviewedItemIds
    ).length
  ) {
    return;
  }
  yield put({type: ActionTypes.ORDER_FEEDBACK_SNOOZED});
  const auth = yield select(getAuth);
  const headers = getBearerHeader(null, auth);
  if (!headers) {
    return;
  }
  try {
    yield call(ReviewsApi.snoozeReviewPrompt, orderFeedback.orderId, headers);
  } catch (e) {
    // Nothing to show: the popup is optional.
  }
}

// Resolves to {ok, message, locked}: `message` is the gateway's GraphQL error,
// shown under that product. `locked` is the service refusing the item for
// good (already reviewed for this order, or within 6 weeks).
function* submitOneReview(review, headers) {
  try {
    const result = yield call(
      ReviewsApi.submitReview,
      buildReviewVariables(review),
      headers
    );
    if (result && result.errors && result.errors.length > 0) {
      const {message, code} = getReviewError(result.errors);
      return {ok: false, message, locked: isLockingReviewCode(code)};
    }
    return {ok: true, message: null, locked: false};
  } catch (e) {
    const {message, code} = getReviewError(
      e.response && e.response.data && e.response.data.errors
    );
    return {ok: false, message, locked: isLockingReviewCode(code)};
  }
}

export function* submitOrderFeedback(action) {
  const {reviews} = action;
  const auth = yield select(getAuth);
  const orderFeedback = yield select(getOrderFeedbackState);
  const headers = getBearerHeader(null, auth);
  if (!headers) {
    yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
    return;
  }
  const analytics = {
    itemCount: orderFeedback.items.length,
    ratedCount: reviews.length,
    commentCount: reviews.filter(review => !!review.comment).length,
  };
  GoogleAnalyticsHelper.trackEvent(
    ANALYTICS_EVENTS.ORDER_FEEDBACK_SUBMIT,
    analytics
  );
  const hasUnrated =
    getUnratedItemIds(
      orderFeedback.items,
      reviews,
      orderFeedback.alreadyReviewedItemIds
    ).length > 0;

  const outcomes = yield all(
    reviews.map(review => call(submitOneReview, review, headers))
  );
  // The server asks about the unrated ones again after the snooze. Only once
  // the submits are done, as it counts what's unrated when it snoozes, and
  // before the result is put, which would make the snooze skip a finished
  // popup.
  if (hasUnrated) {
    yield call(snoozeReviewPrompt);
  }
  const itemIdsWhere = test =>
    reviews
      .filter((review, i) => test(outcomes[i]))
      .map(review => review.itemId);
  const sentItemIds = itemIdsWhere(outcome => outcome.ok);
  const failedItemIds = itemIdsWhere(outcome => !outcome.ok);
  const itemErrors = reviews.reduce(
    (errors, review, i) =>
      outcomes[i].ok
        ? errors
        : {...errors, [review.itemId]: outcomes[i].message},
    {}
  );

  if (failedItemIds.length) {
    GoogleAnalyticsHelper.trackEvent(
      ANALYTICS_EVENTS.ORDER_FEEDBACK_SUBMIT_FAILED,
      {...analytics, failedCount: failedItemIds.length}
    );
    yield put({
      type: ActionTypes.SUBMIT_ORDER_FEEDBACK_FAILED,
      sentItemIds,
      failedItemIds,
      itemErrors,
      alreadyReviewedItemIds: itemIdsWhere(outcome => outcome.locked),
    });
    return;
  }
  GoogleAnalyticsHelper.trackEvent(
    ANALYTICS_EVENTS.ORDER_FEEDBACK_SUBMIT_SUCCESS,
    analytics
  );
  yield put({type: ActionTypes.SUBMIT_ORDER_FEEDBACK_SUCCESS, sentItemIds});
}

export default function* orderFeedbackSaga() {
  yield takeLatest(ActionTypes.GET_ORDER_FEEDBACK_REQUESTED, getOrderFeedback);
  yield takeLatest(
    ActionTypes.SUBMIT_ORDER_FEEDBACK_REQUESTED,
    submitOrderFeedback
  );
  yield takeLatest(
    [ActionTypes.SNOOZE_ORDER_FEEDBACK, ActionTypes.DISMISS_ORDER_FEEDBACK],
    snoozeReviewPrompt
  );
}
